import type {
  AssignmentLine,
  CartAssignment,
  CartForwardBuySummary,
} from '@erp/rpc';
import { groupLinesByCart } from './assignmentCarts';
import { isReleasedStatus } from './releasedStatus';

/**
 * What forward buying currently adds to one cart, from its loaded lines: the
 * committed lines whose effective option is an uplift. Same figures the
 * server's cart summary used (replenishmentTier1 `addForwardBuy`): units are
 * uplift minus base quantity, and the spend change is the line's total minus
 * its base quantity at the frozen baseline unit cost. Null when no line in the
 * cart is forward-bought. A released line's uplift is on its order already.
 */
export function summarizeForwardBuy(
  lines: readonly AssignmentLine[],
): CartForwardBuySummary | null {
  let summary: CartForwardBuySummary | null = null;
  for (const line of lines) {
    const uplift = line.topOption?.forwardBuy;
    if (line.recommendedAction == null || uplift == null) continue;
    if (isReleasedStatus(line.status)) continue;
    summary ??= { linesUplifted: 0, unitsAdded: 0, spendDelta: 0 };
    summary.linesUplifted += 1;
    summary.unitsAdded += uplift.upliftQuantity - uplift.baseQuantity;
    summary.spendDelta +=
      (line.topOption?.totalCost ?? 0) -
      uplift.baseQuantity * uplift.baseUnitCost;
  }
  return summary;
}

/** {@link summarizeForwardBuy} for every cart, keyed by ship-to. */
export function summarizeForwardBuyByCart(
  carts: CartAssignment[],
  lines: AssignmentLine[],
): Map<string, CartForwardBuySummary | null> {
  const byCart = new Map<string, CartForwardBuySummary | null>();
  for (const [shipTo, cartLines] of groupLinesByCart(carts, lines)) {
    byCart.set(shipTo, summarizeForwardBuy(cartLines));
  }
  return byCart;
}
